import { handleRequest } from "../utilities/requestHandler";
import customHttp from "./customHTTP";

const assignedMachineIdsCache = new Map();
const assignedMachineIdsPromiseCache = new Map();

const buildAssignedMachineIdsCacheKey = (siteId, cusId, siteLineId) => {
    const numericSiteId = Number(siteId);
    const numericCusId = Number(cusId);
    const numericSiteLineId = Number(siteLineId);
    return [
        numericSiteId,
        Number.isFinite(numericCusId) && numericCusId > 0 ? numericCusId : "",
        Number.isFinite(numericSiteLineId) && numericSiteLineId > 0 ? numericSiteLineId : "",
    ].join(":");
};

const normalizeAssignedMachineIds = (rawValue) => {
    if (Array.isArray(rawValue)) {
        return rawValue
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0);
    }

    if (typeof rawValue === "string") {
        const trimmed = rawValue.trim();
        if (!trimmed) return [];

        if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
            return trimmed
                .slice(1, -1)
                .split(",")
                .map((value) => Number(String(value || "").trim()))
                .filter((value) => Number.isFinite(value) && value > 0);
        }

        try {
            const parsed = JSON.parse(trimmed);
            return normalizeAssignedMachineIds(parsed);
        } catch (_error) {
            return trimmed
                .split(",")
                .map((value) => Number(String(value || "").trim()))
                .filter((value) => Number.isFinite(value) && value > 0);
        }
    }

    return [];
};

export const getDocumentCategories = () =>
    handleRequest("get", "/documents/categories");

export const getDocuments = (siteId, machineId, cusId, siteLineId) =>
    handleRequest("post", "/documents/getDocuments", {
        site_id: siteId,
        ...(machineId ? { machine_id: machineId } : {}),
        ...(cusId ? { cus_id: cusId } : {}),
        ...(siteLineId ? { site_line_id: siteLineId } : {}),
    });

export const getAssignedMachineIdsForSite = async (siteId, cusId = null, siteLineId = null) => {
    const numericSiteId = Number(siteId);
    if (!Number.isFinite(numericSiteId) || numericSiteId <= 0) {
        return new Set();
    }
    const cacheKey = buildAssignedMachineIdsCacheKey(numericSiteId, cusId, siteLineId);

    if (assignedMachineIdsCache.has(cacheKey)) {
        return assignedMachineIdsCache.get(cacheKey);
    }

    if (assignedMachineIdsPromiseCache.has(cacheKey)) {
        return assignedMachineIdsPromiseCache.get(cacheKey);
    }

    const request = (async () => {
        try {
            const response = await getDocuments(numericSiteId, null, cusId, siteLineId);
            const documents = Array.isArray(response?.data) ? response.data : [];
            const assignedMachineIds = new Set();

            documents.forEach((document) => {
                normalizeAssignedMachineIds(document?.assigned_machines).forEach((machineId) => {
                    assignedMachineIds.add(machineId);
                });
            });

            assignedMachineIdsCache.set(cacheKey, assignedMachineIds);
            return assignedMachineIds;
        } finally {
            assignedMachineIdsPromiseCache.delete(cacheKey);
        }
    })();

    assignedMachineIdsPromiseCache.set(cacheKey, request);
    return request;
};

export const downloadDocument = (documentName) =>
  handleRequest(
    "get",
    `/documents/downloadDocument/${documentName}`,
    null,
    { responseType: "blob" }
  );  
export const getDocumentAccessUrl = (documentName, mode = "view") =>
  handleRequest("get", `/documents/accessUrl/${documentName}`, {
    mode,
  });

export const getDocumentUploadUrl = (payload) =>
  handleRequest("post", "/documents/uploadUrl", payload);

export const getDocumentsSizeEstimate = (payload) =>
  handleRequest("post", "/documents/size", payload);

export const exportDocumentsBulk = async (payload) => {
	try {
		const response = await customHttp.post("/documents/export/bulk", payload, {
			responseType: "blob",
		});
		return response;
	} catch (error) {
		throw error;
	}
};

export const getDocumentsMetaData = (siteId) =>
    handleRequest("post", "/documents/getDocumentsMetaData", {
        site_id: siteId,
    });

export const uploadDocument = (formData, options = {}) =>
    handleRequest("post", "/documents/uploadDocument", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        ...(typeof options === "object" && options ? options : {}),
    });

export const updateDocument = (documentId, formData) =>
	handleRequest("put", `/documents/update/${documentId}`, formData, {
		headers: { "Content-Type": "multipart/form-data" },
	});

export const deleteDocument = (documentId) =>
    handleRequest("delete", `/documents/deleteDocument/${documentId}`);

export const searchMachines = (searchQuery, page, limit = 50) =>
    handleRequest("post", "/machines/search", { q: searchQuery, page, limit });

export const getDocumentAudience = (payload) =>
	handleRequest("post", "/documents/audience", payload);

export const createDocumentCategory = (payload) =>
	handleRequest("post", "/documents/categories", payload);

export const updateDocumentCategory = (categoryId, payload) =>
	handleRequest("put", `/documents/categories/${categoryId}`, payload);

export const deleteDocumentCategory = (categoryId, payload = {}) =>
	handleRequest("delete", `/documents/categories/${categoryId}`, payload);

export const updateCategoryDocuments = (categoryId, payload) =>
	handleRequest("post", `/documents/categories/${categoryId}/documents`, payload);
